// frontend/src/components/EmailReminderRules.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { useGeneticsOptions } from '../hooks/useGeneticsOptions';
import { ConfirmDialog } from './ConfirmDialog';

const EmailReminderRules = () => {
  const { user } = useAuth();
  const toast = useToast();
  const { confirm, confirmDialogProps } = useConfirm();
  const [rules, setRules] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  // Only the initial load failure stays inline: it explains an empty list,
  // and a toast would fade leaving the page looking like "you have no rules".
  // Every save/delete/toggle outcome is an action result and goes to a toast.
  const [error, setError] = useState('');
  const { locusOptions: availableLoci, alleleOptions: availableAlleles } = useGeneticsOptions();

  // Form state
  const [formData, setFormData] = useState({
    rule_name: '',
    entity_type: 'mice',
    conditions: [],
    reminder_text: '',
    is_active: true
  });

  useEffect(() => {
    fetchRules();
  }, []);

  // Add useEffect to handle entity type changes
  useEffect(() => {
    // When entity type changes, clear conditions to avoid invalid field references
    if (formData.entity_type) {
      const currentFields = getFieldOptions(formData.entity_type);
    }
  }, [formData.entity_type, availableLoci]); // Add availableLoci as dependency

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/email-reminder-rules`);
      setRules(response.data);
    } catch (err) {
      setError('Failed to fetch email reminder rules');
    }
  };

  const getFieldOptions = (entityType) => {
    const baseFields = {
      mice: [
        { field: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female'] },
        { field: 'age_weeks', label: 'Age (weeks)', type: 'number' },
        { field: 'status', label: 'Status', type: 'select', options: ['Active', 'Archived'] },
        { field: 'physical_tag', label: 'Physical Tag', type: 'text' },
        { field: 'notes', label: 'Notes', type: 'text' }
      ],
      cages: [
        { field: 'status', label: 'Status', type: 'select', options: ['Active', 'Archived'] },
        { field: 'capacity', label: 'Capacity', type: 'number' },
        { field: 'location', label: 'Location', type: 'text' },
        { field: 'mouse_count', label: 'Mouse Count', type: 'number' },
        { field: 'notes', label: 'Notes', type: 'text' }
      ],
      litters: [
        { field: 'age_days', label: 'Age (days)', type: 'number' },
        { field: 'status', label: 'Status', type: 'select', options: ['Born', 'Weaned', 'Dispersed', 'Archived'] },
        { field: 'initial_pup_count', label: 'Initial Pup Count', type: 'number' },
        { field: 'weaned_pup_count', label: 'Weaned Pup Count', type: 'number' },
        { field: 'notes', label: 'Notes', type: 'text' }
      ]
    };

    let fields = baseFields[entityType] || [];

    // Add genotype fields for mice
    if (entityType === 'mice') {
      
      if (availableLoci && availableLoci.length > 0) {
        const genotypeFields = availableLoci.map(locus => ({
          field: `genotype.${locus.locus_name}`,
          label: `Genotype: ${locus.locus_name}`,
          type: 'genotype',
          locus: locus.locus_name
        }));
        
        fields = [...fields, ...genotypeFields];
      } else {
      }
    }

    return fields;
  };

  const getOperatorOptions = (fieldType) => {
    const operators = {
      text: [
        { value: 'equals', label: 'equals' },
        { value: 'contains', label: 'contains' },
        { value: 'starts_with', label: 'starts with' },
        { value: 'ends_with', label: 'ends with' }
      ],
      number: [
        { value: 'equals', label: 'equals' },
        { value: 'greater_than', label: 'greater than' },
        { value: 'less_than', label: 'less than' },
        { value: 'greater_than_or_equal', label: 'greater than or equal to' },
        { value: 'less_than_or_equal', label: 'less than or equal to' }
      ],
      select: [
        { value: 'equals', label: 'equals' },
        { value: 'not_equals', label: 'does not equal' }
      ],
      genotype: [
        { value: 'contains', label: 'contains' },
        { value: 'equals', label: 'equals exactly' }
      ]
    };
    return operators[fieldType] || operators.text;
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, {
        field: '',
        operator: 'equals',
        value: ''
      }]
    }));
  };

  const updateCondition = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) => 
        i === index ? { ...condition, [key]: value } : condition
      )
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      rule_name: '',
      entity_type: 'mice',
      conditions: [],
      reminder_text: '',
      is_active: true
    });
    setEditingRule(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingRule) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-reminder-rules/${editingRule.id}`,
          formData
        );
        toast.success('Email reminder rule updated successfully!');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/email-reminder-rules`,
          formData
        );
        toast.success('Email reminder rule created successfully!');
      }
      
      resetForm();
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save email reminder rule');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rule) => {
    setFormData({
      rule_name: rule.rule_name,
      entity_type: rule.entity_type,
      conditions: rule.conditions,
      reminder_text: rule.reminder_text,
      is_active: rule.is_active
    });
    setEditingRule(rule);
    setShowAddForm(true);
  };

  const handleDelete = async (ruleId, ruleName) => {
    if (!await confirm(`Are you sure you want to delete the rule "${ruleName}"?`, {
      confirmLabel: 'Delete',
      danger: true
    })) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/email-reminder-rules/${ruleId}`);
      toast.success('Email reminder rule deleted successfully!');
      fetchRules();
    } catch (err) {
      toast.error('Failed to delete email reminder rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/email-reminder-rules/${rule.id}`,
        { ...rule, is_active: !rule.is_active }
      );
      fetchRules();
    } catch (err) {
      toast.error('Failed to update rule status');
    }
  };

  const renderConditionValue = (condition, index) => {
    const fieldOptions = getFieldOptions(formData.entity_type);
    const fieldConfig = fieldOptions.find(f => f.field === condition.field);
    
    
    if (!fieldConfig) {
      return (
        <input
          type="text"
          value={condition.value}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
          placeholder="Value"
          className="form-control"
        />
      );
    }

    if (fieldConfig.type === 'select') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
          className="form-control"
        >
          <option value="">Select value</option>
          {fieldConfig.options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (fieldConfig.type === 'genotype') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <input
            type="text"
            value={condition.value}
            onChange={(e) => updateCondition(index, 'value', e.target.value)}
            placeholder="e.g., Cre/+, fl/fl, +/+"
            className="form-control"
          />
        </div>
      );
    }

    return (
      <input
        type={fieldConfig.type === 'number' ? 'number' : 'text'}
        value={condition.value}
        onChange={(e) => updateCondition(index, 'value', e.target.value)}
        placeholder="Value"
        className="form-control"
      />
    );
  };

  const formatConditions = (conditions) => {
    return conditions.map(condition => {
      if (condition.field.startsWith('genotype.')) {
        const locus = condition.field.split('.')[1];
        return `${locus} locus ${condition.operator} "${condition.value}"`;
      }
      return `${condition.field} ${condition.operator} "${condition.value}"`;
    }).join(' AND ');
  };

  // Add debug component to show current state
  const renderDebugInfo = () => {
    if (process.env.NODE_ENV !== 'development') return null;
    
    return (
      <div style={{ 
        background: '#f0f0f0', 
        padding: '1rem', 
        marginBottom: '1rem', 
        borderRadius: '4px',
        fontSize: '0.8rem'
      }}>
        <strong>Debug Info:</strong>
        <div>Available Loci: {availableLoci?.length || 0}</div>
        <div>Available Alleles: {availableAlleles?.length || 0}</div>
        <div>Current Entity Type: {formData.entity_type}</div>
        <div>Available Fields: {getFieldOptions(formData.entity_type).length}</div>
        <div>Genotype Fields: {getFieldOptions(formData.entity_type).filter(f => f.type === 'genotype').length}</div>
      </div>
    );
  };

  return (
    <div className="email-reminder-rules">
      {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
      {error && <div className="error">{error}</div>}

      {renderDebugInfo()}

      <div className="action-buttons" style={{ marginBottom: '1rem' }}>
        <button 
          className="primary-button"
          onClick={() => setShowAddForm(true)}
          disabled={loading}
        >
          + Add New Rule
        </button>
      </div>

      {showAddForm && (
        <div className="add-rule-form" style={{ 
          background: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          marginBottom: '1.5rem',
          border: '1px solid #dee2e6'
        }}>
          <h4>{editingRule ? 'Edit Rule' : 'Create New Email Reminder Rule'}</h4>
          
          <form onSubmit={handleSubmit}>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label>Rule Name:</label>
                <input
                  type="text"
                  value={formData.rule_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, rule_name: e.target.value }))}
                  placeholder="e.g., 6-week-old male WT mice"
                  required
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label>Entity Type:</label>
                <select
                  value={formData.entity_type}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    entity_type: e.target.value,
                    conditions: [] // Reset conditions when entity type changes
                  }))}
                  className="form-control"
                >
                  <option value="mice">Mice</option>
                  <option value="cages">Cages</option>
                  <option value="litters">Litters</option>
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Conditions:</label>
              {formData.entity_type === 'mice' && availableLoci?.length > 0 && (
                <div style={{ 
                  background: '#e8f5e8', 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  marginBottom: '0.5rem',
                  fontSize: '0.85rem'
                }}>
                  <strong>💡 Genotype Examples:</strong>
                  <br />• "Sox9 locus contains Cre/+" - finds mice with Cre/+ in Sox9 locus
                  <br />• "Kras locus equals fl/fl" - finds mice with exactly fl/fl in Kras locus
                </div>
              )}
              {formData.conditions.map((condition, index) => (
                <div key={index} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 2fr auto', 
                  gap: '0.5rem', 
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  padding: '0.5rem',
                  background: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px'
                }}>
                  <select
                    value={condition.field}
                    onChange={(e) => updateCondition(index, 'field', e.target.value)}
                    className="form-control"
                  >
                    <option value="">Select field</option>
                    {getFieldOptions(formData.entity_type).map(field => (
                      <option 
                        key={field.field} 
                        value={field.field}
                        style={{ 
                          backgroundColor: field.type === 'genotype' ? '#e3f2fd' : 'white',
                          fontWeight: field.type === 'genotype' ? 'bold' : 'normal'
                        }}
                      >
                        {field.label}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                    className="form-control"
                  >
                    {getOperatorOptions(
                      getFieldOptions(formData.entity_type).find(f => f.field === condition.field)?.type || 'text'
                    ).map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  
                  {renderConditionValue(condition, index)}
                  
                  <button
                    type="button"
                    onClick={() => removeCondition(index)}
                    className="secondary-button"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={addCondition}
                className="secondary-button"
                style={{ marginTop: '0.5rem' }}
              >
                + Add Condition
              </button>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Reminder Text:</label>
              <textarea
                value={formData.reminder_text}
                onChange={(e) => setFormData(prev => ({ ...prev, reminder_text: e.target.value }))}
                placeholder="Describe what this reminder is for (e.g., 'Time to check 6-week-old male WT mice for experiments')"
                rows="3"
                required
                className="form-control"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group toggle-group" style={{ marginBottom: '1rem' }}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active (reminders will be sent)
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading} className="primary-button">
                {loading ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
              </button>
              <button type="button" onClick={resetForm} className="secondary-button">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="rules-list">
        <h4>Existing Email Reminder Rules ({rules.length})</h4>
        
        {rules.length === 0 ? (
          <div className="no-data">
            No email reminder rules created yet. Click "Add New Rule" to get started.
          </div>
        ) : (
          <div className="rules-grid">
            {rules.map(rule => (
              <div key={rule.id} className="rule-card" style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                background: rule.is_active ? 'white' : '#f8f9fa'
              }}>
                <div className="rule-header" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <h5 style={{ margin: 0, color: rule.is_active ? '#333' : '#666' }}>
                    {rule.rule_name}
                  </h5>
                  <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`} style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    background: rule.is_active ? '#d4edda' : '#f8d7da',
                    color: rule.is_active ? '#155724' : '#721c24'
                  }}>
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="rule-details" style={{ marginBottom: '1rem' }}>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Entity:</strong> {rule.entity_type}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Conditions:</strong> {formatConditions(rule.conditions)}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Reminder:</strong> {rule.reminder_text}
                  </p>
                </div>
                
                <div className="rule-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className="secondary-button"
                    onClick={() => handleEdit(rule)}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
                  >
                    Edit
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => handleToggleActive(rule)}
                    style={{ 
                      padding: '0.25rem 0.75rem', 
                      fontSize: '0.8rem',
                      background: rule.is_active ? '#ffc107' : '#28a745',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    {rule.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => handleDelete(rule.id, rule.rule_name)}
                    style={{ 
                      padding: '0.25rem 0.75rem', 
                      fontSize: '0.8rem',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailReminderRules;